/** @format */

import React from 'react';
import PropTypes from 'prop-types';
import handleError from './handle_error';
import handleInputError from './handle_input_error';
import {Select} from '@sttark/design-system';
import {Select as CustomSelect, SelectItem, SelectGroup, SelectLabel} from '@/components/ui/select';

/**
 * Wrap an input with our input error handler
 * Meant to be as flexible and transparent as possible
 * Steps for usage.
 * <SelectWithErrorHandler
 *    className="form-control"
 *    type="text"
 *    error={props.error}
 *    errorPath="company"
 *    value={props.company || ''}
 *    onChange={props.onInputChange.bind(this, 'user.company')}
 * />
 * Import this component and give it all the normal values you would for an input but also add
 * error - This is the error object that contains the messages for fields {'user_detail': { 'text_number' : {'_empty':'hey! do not leave me empty'}}}
 * errorPath - This will look for the error at that endpoint. So for user_detail text_number error, you would pass user_detail.text_number
 */

const SelectWithErrorHandler = props => {
    let {
        className,
        hasError,
        errorMessage,
        emptyText = props.emptyText || '--',
        options,
        grouped,
        initialValue,
        value,
        onChange,
        onChangeValue,
        newestds,
        name,
        ...inputProps
    } = props;

    if (hasError) {
        if (className && className.length > 0) {
            className = className.split(' ');
        } else {
            className = [];
        }
        className.push('is-invalid');
        inputProps.className = className.join(' ');
    } else if (className) {
        inputProps.className = className;
    }

    const _getErrorFeedback = (hasError, errorMessage) => {
        if (hasError) {
            return (
                <div style={{fontSize: '.9em', color: 'red', display: 'block'}}>{errorMessage}</div>
            );
        }

        return;
    };

    let selectOptions = null;

    if (!newestds) {
        // Create grouped options using custom Select components
        if (grouped && options) {
            selectOptions = Object.keys(options).map(group => (
                <SelectGroup key={'group_' + group}>
                    <SelectLabel>{group}</SelectLabel>
                    {Object.keys(options[group]).map(id => {
                        const item = options[group][id];
                        if (typeof item === 'string') {
                            return (
                                <SelectItem key={'option_' + id} value={id}>
                                    {item}
                                </SelectItem>
                            );
                        }
                        return (
                            <SelectItem key={'option_' + item.id} value={item.id}>
                                {item.label}
                            </SelectItem>
                        );
                    })}
                </SelectGroup>
            ));
        } else if (options) {
            // the options might be an array of string, in this case we need to create the options according to the proper type
            if (options[0] && typeof options[0] === 'string') {
                options = options.map(option => {
                    return {id: option, label: option};
                });
            }
            selectOptions = options.map(option => (
                <SelectItem key={'option_' + option.id + option.label} value={option.id}>
                    {option.label}
                </SelectItem>
            ));
        } else {
            selectOptions = <SelectItem value="">You are missing options</SelectItem>;
        }
    } else if (options && newestds) {
        // used by @sttark/design-system <Select>
        selectOptions = [];
        options.map(option => {
            selectOptions.push({label: option.label, value: option.id});
        });
    }

    // Go ahead and just send the value, not the full event
    onChange = onChangeValue ? ({target: {value}}) => onChangeValue(value) : onChange;

    if (newestds) {
        delete inputProps.className;
    }

    // TODO: This provides a select element using the design system, the prop name can be improved
    if (props.newestds) {
        delete inputProps.className;

        const selectOptions = [];
        options.map(option => {
            selectOptions.push({label: option.label, value: option.id});
        });

        return (
            <Select
                {...inputProps}
                onChange={onChange}
                error={props.errorMessage && props.errorMessage !== '' ? props.errorMessage : null}
                options={selectOptions}
                initialValue={initialValue}
            />
        );
    }

    return (
        <>
            <CustomSelect
                placeholder={emptyText}
                className={className}
                onChange={onChange}
                value={value}
                name={name}
                {...inputProps}>
                {selectOptions}
            </CustomSelect>
            {_getErrorFeedback(hasError, errorMessage)}
        </>
    );
};

SelectWithErrorHandler.propTypes = {
    error: PropTypes.oneOfType([PropTypes.bool, PropTypes.object])
};

export default handleError(handleInputError(SelectWithErrorHandler));
